import { create } from "zustand";

/**
 * How many InfoDialog instances are currently open, app-wide (GRA-146). A
 * counter rather than a boolean: dialogs are not expected to nest, but a
 * counter degrades safely if two ever mount at once (e.g. during a fast
 * re-render), where a boolean set by whichever closes last would go wrong.
 *
 * Every global keyboard shortcut in the app (Predict guess keys, Trade's
 * direction/Enter handlers, LoopShell's Next and replay-step keys, the Tools
 * drawer hotkeys, the settings quick-key, the daily challenge's share-image
 * key) calls `isDialogOpen()` at the top of its handler and bails out while
 * it's true. That is what stops a key meant for the dialog (Escape, Tab) or a
 * stray one (an arrow key, Enter) from reaching the game underneath it: the
 * bug this ticket exists to fix was an ArrowUp/Enter/Enter sequence, typed
 * while the data dialog had focus, flipping Trade's direction and placing a
 * trade behind the dialog.
 */
type DialogState = {
  openCount: number;
  push: () => void;
  pop: () => void;
};

export const useDialogStore = create<DialogState>((set) => ({
  openCount: 0,
  push: () => set((s) => ({ openCount: s.openCount + 1 })),
  pop: () => set((s) => ({ openCount: Math.max(0, s.openCount - 1) })),
}));

/** Read outside React (inside a `window` keydown handler) without subscribing. */
export function isDialogOpen(): boolean {
  return useDialogStore.getState().openCount > 0;
}
